import { useState, useEffect } from "react";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Heart, Clock, BarChart3, Sparkles, ScanFace } from "lucide-react";
import { isWebAuthnSupported, getStoredCredentialId, loginWithBiometric, hasBiometricHint } from "../lib/webauthn";
import { showGoogleSignIn } from "../lib/platform";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import logger from "../lib/logger";
import useSeo from "../hooks/useSeo";

const LandingPage = () => {
  const { user, login, setUser } = useAuth();
  const navigate = useNavigate();
  const [hasBiometric, setHasBiometric] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  useSeo({
    title: "Sign in to Bilateral Calm — Guided bilateral stimulation",
    description:
      "Sign in to access Bilateral Calm's library of guided bilateral-stimulation practices for anxiety, sleep, stage fright, and burnout.",
    path: "/login",
  });

  useEffect(() => {
    async function check() {
      const supported = await isWebAuthnSupported();
      const credId = getStoredCredentialId();
      const hint = hasBiometricHint();
      setHasBiometric(supported && (!!credId || hint));
    }
    check();
  }, []);

  if (user) {
    window.location.href = '/dashboard';
    return null;
  }

  const handleBiometricLogin = async () => {
    setBiometricLoading(true);
    try {
      const userData = await loginWithBiometric();
      setUser(userData);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      logger.error("Biometric login failed:", error);
      toast.error("Biometric login failed. Please sign in with email.");
    } finally {
      setBiometricLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative" data-testid="landing-page">
      {/* Fixed wallpaper — cornflowers + sunset wheat field, with a cream
          wash on top so all content stays legible. The wallpaper stays put
          while the page scrolls. Anchored to the bottom of the image so the
          flower-rich foreground gets prime real estate; a subtle saturation
          boost makes the blooms pop through the wash. */}
      <div
        className="fixed inset-0 -z-10 bg-cover bg-bottom"
        style={{
          backgroundImage: "url('/images/learn-more-wallpaper.webp')",
          filter: "saturate(1.18) brightness(1.06)",
        }}
        aria-hidden
      />
      <div
        className="fixed inset-0 -z-10"
        style={{ backgroundColor: "rgba(251,248,241,0.72)" }}
        aria-hidden
      />
      {/* Header */}
      <header className="bg-[#FBF8F1]/80 backdrop-blur-md border-b border-slate-100/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center"
            data-testid="learn-home-btn"
          >
            <img src="/logo-bilateral-calm.png" alt="Bilateral Calm" className="h-28 sm:h-32 w-auto" />
          </button>
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              onClick={() => navigate("/practices")}
              className="hidden sm:inline-flex text-sm text-[#4A5568] hover:text-[#1B2349] font-medium px-3 py-2 rounded-full"
              data-testid="learn-nav-practices-btn"
            >
              Practice Library
            </button>
            <button
              onClick={() => navigate("/pricing")}
              className="hidden sm:inline-flex text-sm text-[#4A5568] hover:text-[#1B2349] font-medium px-3 py-2 rounded-full"
              data-testid="learn-nav-plans-btn"
            >
              Plans
            </button>
            <button
              onClick={() => navigate("/about")}
              className="hidden md:inline-flex text-sm text-[#4A5568] hover:text-[#1B2349] font-medium px-3 py-2 rounded-full"
              data-testid="learn-nav-about-btn"
            >
              About
            </button>
            <button
              onClick={() => navigate("/faq")}
              className="hidden sm:inline-flex text-sm text-[#4A5568] hover:text-[#1B2349] font-medium px-3 py-2 rounded-full"
              data-testid="learn-nav-faq-btn"
            >
              FAQ
            </button>
            {hasBiometric && (
              <Button
                data-testid="biometric-login-btn-header"
                onClick={handleBiometricLogin}
                disabled={biometricLoading}
                variant="outline"
                className="border-[#78C0D8] text-[#78C0D8] hover:bg-[#78C0D8] hover:text-white rounded-full"
              >
                <ScanFace className="w-4 h-4 mr-2" />
                {biometricLoading ? "Verifying..." : "Face ID"}
              </Button>
            )}
            <Button
              data-testid="login-btn-google"
              onClick={login}
              variant="outline"
              className={`rounded-full border-[#1B2349] text-[#1B2349] hover:bg-[#1B2349] hover:text-white ${
                showGoogleSignIn() ? "hidden lg:inline-flex" : "hidden"
              }`}
            >
              Sign in with Google
            </Button>
            <Button
              data-testid="login-btn"
              onClick={() => navigate("/sign-in")}
              className="btn-primary"
            >
              Sign in
            </Button>
          </div>
        </div>
      </header>

      {/* Tagline hero */}
      <section className="px-6 pt-16 sm:pt-24 pb-4 max-w-5xl mx-auto text-center" data-testid="learn-tagline-hero">
        <h1 className="font-outfit font-semibold text-[2.5rem] leading-[1.05] sm:text-5xl lg:text-6xl text-[#1B2349] tracking-tight">
          An easier, kinder way to{" "}
          <em className="italic font-extrabold text-[#3090A8]">regulate</em>{" "}
          your nervous system.
        </h1>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="font-outfit font-semibold text-2xl sm:text-3xl text-[#1B2349] mb-4">
              Healing Through Bilateral Stimulation
            </h2>
            <p className="text-[#718096] max-w-2xl mx-auto">
              Our guided practices use bilateral stimulation to help your brain 
              process difficult emotions and experiences naturally.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Heart className="w-6 h-6" />,
                title: "Practices Made for You",
                description: "Targeted support for worry, overwhelm, tension, old stories, stage fright, sleeplessness, blues, self-worth, boundaries, grief, anger, performance, and habits."
              },
              {
                icon: <Clock className="w-6 h-6" />,
                title: "Work at Your Pace",
                description: "Guided tasks for each practice. Pause anytime and return when you're ready."
              },
              {
                icon: <BarChart3 className="w-6 h-6" />,
                title: "Track Your Progress",
                description: "Monitor your journey with detailed progress tracking and practice history."
              }
            ].map((feature) => (
              <div 
                key={feature.title}
                className={`bg-white/65 backdrop-blur-md rounded-2xl p-6 shadow-[0_4px_20px_rgba(27,35,73,0.06)] border border-white/40 card-hover animate-fade-in-up`}
              >
                <div className="w-10 h-10 bg-[#E8F0E7] rounded-xl flex items-center justify-center text-[#3090A8] mb-4">
                  {feature.icon}
                </div>
                <h3 className="font-outfit font-medium text-base text-[#1B2349] mb-2">
                  {feature.title}
                </h3>
                <p className="text-[#4A5568] text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Practices Preview */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-outfit font-semibold text-2xl sm:text-3xl text-[#1B2349] mb-4">
              Healing Practices Curated Just for You
            </h2>
            <p className="text-[#1B2349]/80 max-w-2xl mx-auto">
              Guided practices designed to address your unique quest for peace.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5 justify-items-center max-w-5xl mx-auto">
            {[
              { slug: "quiet_the_worry",          title: "Quiet The Worry",          color: "#3090A8", desc: "Calm anxious, looping thoughts.",          image: "/images/interventions/quiet-the-worry.webp" },
              { slug: "overcome_overwhelm",       title: "Overcome Your Overwhelm",  color: "#78C0D8", desc: "Untangle when too much piles up.",         image: "/images/interventions/overcome-overwhelm.webp" },
              { slug: "dial_down_tension",        title: "Dial Down Body Tension",   color: "#E2B887", desc: "Release tightness held in the body.",      image: "/images/interventions/dial-down-tension.webp" },
              { slug: "healing_old_stories",      title: "Healing Old Stories",      color: "#D98880", desc: "Loosen the grip of past wounds.",          image: "/images/interventions/healing-old-stories.webp" },
              { slug: "beat_stage_fright",        title: "Beat Stage Fright",        color: "#9F7AEA", desc: "Settle nerves before you step up.",        image: "/images/interventions/beat-stage-fright.webp" },
              { slug: "fear_of_sleeplessness",    title: "Fear Of Sleeplessness",    color: "#78C0D8", desc: "Quiet the mind so rest can come.",         image: "/images/interventions/fear-of-sleeplessness.webp" },
              { slug: "shake_off_sadness",        title: "Shake Off The Blues",      color: "#3090A8", desc: "Lift a low, heavy mood.",                  image: "/images/interventions/shake-off-sadness.webp" },
              { slug: "boost_self_worth",         title: "Boost Your Self-Worth",    color: "#E2B887", desc: "Reclaim a steady sense of value.",         image: "/images/interventions/boost-self-worth.webp" },
              { slug: "hold_boundaries",          title: "Hold Your Boundaries",     color: "#D98880", desc: "Stand kindly in your own ground.",         image: "/images/interventions/hold-boundaries.webp" },
              { slug: "relief_from_grief",        title: "Relief From Grief",        color: "#9F7AEA", desc: "Soften the weight of loss.",               image: "/images/interventions/relief-from-grief.webp" },
              { slug: "calm_your_anger",          title: "Calm Your Anger",          color: "#78C0D8", desc: "Cool the heat without pushing it down.",    image: "/images/interventions/calm-your-anger.webp" },
              { slug: "enhance_performance",      title: "Enhance Your Performance", color: "#3090A8", desc: "Show up focused when it matters.",         image: "/images/interventions/enhance-performance.webp" },
              { slug: "new_habit_new_you",        title: "New Habit, New You",       color: "#E2B887", desc: "Anchor a lasting change.",                 image: "/images/interventions/new-habit-new-you.webp" },
              { slug: "headache_relief",          title: "Headache Relief",          color: "#D98880", desc: "Ease tension headaches at the source.",    image: "/images/interventions/headache-relief.webp" },
              { slug: "nightmare_reset",          title: "Nightmare Reset",          color: "#9F7AEA", desc: "Settle after a nightmare and rewrite it.", image: "/images/interventions/nightmare-reset.webp" },
              { slug: null,                       title: "Daily Calming Practices",  color: "#3090A8", desc: "Bilateral audio sessions for any moment.", image: "/images/library/daily-calming-card.webp" }
            ].map((item) => (
              <button
                key={item.title}
                type="button"
                onClick={() => item.slug ? navigate(`/practices/${item.slug}`) : navigate("/pricing")}
                className="group/p text-left w-full max-w-[220px] rounded-2xl overflow-hidden bg-white/55 backdrop-blur-md border border-white/40 hover:bg-white/70 hover:-translate-y-1 transition-all duration-300 shadow-[0_4px_20px_rgba(27,35,73,0.06)] hover:shadow-[0_10px_30px_rgba(27,35,73,0.12)] focus:outline-none focus:ring-2 focus:ring-[#3090A8]/50"
                data-testid={`learn-tile-${item.slug || "library"}`}
              >
                <div
                  className="aspect-square w-full bg-cover bg-center"
                  style={{ backgroundImage: `url('${item.image}')` }}
                />
                <div className="p-3 sm:p-4">
                  <h3 className="font-outfit font-medium text-sm sm:text-base text-[#1B2349] leading-tight">{item.title}</h3>
                  <p className="text-[#4A5568] text-xs sm:text-sm mt-1 leading-relaxed">{item.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-outfit font-semibold text-2xl sm:text-3xl text-[#1B2349] mb-4">
            Ready to Begin Your Journey?
          </h2>
          <p className="text-[#718096] mb-8">
            Start your first practice today and discover the power of 
            bilateral stimulation for emotional healing.
          </p>
          <Button
            data-testid="cta-sign-in-btn"
            onClick={() => navigate("/pricing")}
            className="btn-primary text-lg px-10 py-4"
          >
            Start sign up
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-slate-100">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center">
            <img src="/logo-bilateral-calm.png" alt="Bilateral Calm" className="h-20 sm:h-24 w-auto" />
          </div>
          <p className="text-[#718096] text-sm">
            © 2026 Mood and Sleep Clinic LLC · Bilateral Calm™ · <a href="mailto:mscwellness@protonmail.com" className="underline underline-offset-2 hover:text-[#1B2349]">mscwellness@protonmail.com</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
